import { Duration } from 'luxon';
import { useEffect, useState } from 'react';
import { percentToTimestamp, useTimelineDrag } from '../../../hooks/useTimelineDrag';
import { SlideTimelineEvent, Timeline } from '../../../parser';

export const SlidesTrack = ({
    timeline,
    duration,
    isActive,
    onClick
}: {
    timeline: Timeline;
    duration: Duration;
    isActive: boolean;
    onClick: (event: SlideTimelineEvent) => void;
}) => {
    const [slides, setSlides] = useState<SlideTimelineEvent[]>([]);

    useEffect(() => {
        if (!timeline?.slides || duration.toMillis() === 0) {
            setSlides([]);
            return;
        }

        setSlides(timeline.slides);
    }, [timeline?.slides, duration]);

    const drag = useTimelineDrag<SlideTimelineEvent>(
        (slide, position) => {
            // Video time works in seconds, so we need to round the timestamp to the nearest second
            slide.timestamp = percentToTimestamp(duration, position);
            // Check if there are two slides with the same timestamp
            const duplicate = timeline.slides.find((s) => s.timestamp.equals(slide.timestamp) && s !== slide);
            if (!!duplicate) {
                if (slide.timestamp.as('seconds') > 0) {
                    slide.timestamp = slide.timestamp.minus({ seconds: 1 });
                } else {
                    slide.timestamp = slide.timestamp.plus({ seconds: 1 });
                }
            }
            setSlides([...timeline.slides.sort((a, b) => a.timestamp.toMillis() - b.timestamp.toMillis())]);
        },
        (slide) => {
            slide.changed = true;
            timeline.changed = true;
            timeline.slides = [...timeline.slides];
        }
    );

    const onDragStart = (e: React.MouseEvent<HTMLButtonElement>, slide: SlideTimelineEvent) =>
        drag.begin(e, slide, (slide.timestamp.toMillis() / duration.toMillis()) * 100);

    return (
        <div className={`track track-slides ${isActive ? 'track-active' : ''} row-start-3`}>
            {slides.map((slide, index) => {
                let width = 0;
                if (index === slides.length - 1) {
                    width = ((duration.toMillis() - slides[index].timestamp.toMillis()) / duration.toMillis()) * 100;
                } else {
                    width = ((slides[index + 1].timestamp.toMillis() - slides[index].timestamp.toMillis()) / duration.toMillis()) * 100;
                }

                return (
                    <div
                        key={index}
                        style={{ left: `${(slide.timestamp.toMillis() / duration.toMillis()) * 100}%`, width: `${width}%` }}
                        onClick={() => onClick(slide)}
                        className={slide.changed ? 'changed' : ''}
                    >
                        <button onMouseDown={(event) => onDragStart(event, slide)}></button>
                        <img src={slide.url} draggable={false} />
                    </div>
                );
            })}
        </div>
    );
};
